import type { Dispatch, MouseEventHandler, SetStateAction } from 'react';

export type Operator =
  | NumberOperator
  | MultiSelectionOperator
  | MetricsOperator
  | BooleanOperator
  | FreeFormSearchOperator;

export interface FilterProps {
  name: StandardFilterType | string;
  value?: string | string[] | boolean;
  operator: Operator | undefined;
  category: FilterCategory;
  handleChange: (
    operator: Operator,
    value: string | string[] | boolean,
  ) => void;
  handleRemove: MouseEventHandler<HTMLButtonElement>;
  metric?: Metric;
  options?: OptionsType[];
  operators?: MultiSelectionOperator[];
  readablePluralOpt?: boolean;
  hideTagBorder?: boolean;
}

export interface StandardFilterProps extends FilterProps {
  handlePopupOpen: () => void;
  handlePopupClose: () => void;
  isPopupOpen: boolean;
  setIsFilterFocussed: Dispatch<SetStateAction<boolean>>;
}

export interface Metric {
  name: string;
  type: MetricInputType;
  operators: string[];
  allowed_values?: string[];
}

export interface MetricsFilterProps extends StandardFilterProps {
  metric: Metric;
}

export type FilterCategory = 'standard' | 'metrics' | 'sort';

export interface ParsedFilterItem {
  name: StandardFilterType | string;
  operator: Operator | undefined;
  value?: string | string[] | boolean;
  category: FilterCategory;
}

export enum StandardFilterType {
  TAGS = 'tags',
  CREATED_AT = 'created_at',
  // Separating this out from CREATED_AT to allow for user timezone date filtering
  CREATED_AT_USER_TIMEZONE = 'created_at_user_timezone',
  CLIENT_ENV = 'client_env',
  VERSION = 'version',
  FROM_NUMBER = 'from_number',
  DURATION = 'duration',
  POLY_SCORE = 'poly_score',
  SORT = 'sort',
  OFFSET = 'offset',
  LANGUAGE = 'language',
  LANGUAGE_FREEFORM = 'language_freeform',
  IN_PROGRESS = 'in_progress',
  STATUSES = 'statuses',
  FLOWS = 'flows',
  LAST_MODIFIED = 'last_modified',
  REVIEWERS = 'reviewers',
  REVIEWED = 'reviewed',
  VARIANT = 'variant_id',
  VARIANT_FREEFORM = 'variant_id_freeform',
  CALL_SID = 'conversation_id',
  EXTENSION = 'extension',
  FALLBACK_ROUTE = 'fallback_route',
  FUNCTION_CALLS = 'function_calls',
  CALL_HANDOFF = 'HANDOFF_REASON',
  HANDOFF_TO = 'HANDOFF_TO',
  SORT_ALPHABETICALLY = 'sort_alphabetically',
  SORT_LAST_UPDATED = 'sort_last_updated',
  SORT_INACTIVE = 'sort_inactive',
  SMS_EVENTS = 'sms_events',
  QA = 'QA',
  CONTENT_FILTER_TRIGGERED = 'content_filters_triggered',
  TO_NUMBER = 'to_number',
  CACHED_AT = 'cached_at',
  TRANSCRIPT = 'cache_transcript',
  HIT_COUNT = 'hit_count',
  VOICE_ID = 'provider_voice_id',
  CACHE_ID = 'id',
  METRIC_NAME = 'metric_name',
  METRIC_TYPE = 'metric_type',
  METRIC_STATUS = 'metric_status',
  BATCH_ID = 'batch_id',
  LATENCY = 'latency',
  // This CHANNEL label below is a combination of the conversation.direction and conversation.channel fields
  // Relevant ticket: CORE-190
  CHANNEL = 'channel',
  DIRECTION_CHANNEL = 'direction_channel',
  OUTBOUND_CALL_STATUS = 'OUTBOUND_CALL_STATUS',
  INTERRUPTIONS = 'interruptions',
  AB_GROUP_ASSIGNMENT = 'AB_GROUP_ASSIGNMENT',
  ADDED_AT = 'added_at',
  ADDED_BY = 'added_by',
  LATEST_OUTCOME = 'latest_outcome',
  INTEGRATION_CATEGORY = 'integration_category',
}

export enum ReadableFilterType {
  tags = 'Tags',
  created_at = 'Start date',
  created_at_user_timezone = 'Start date',
  from_number = 'Contact',
  to_number = 'To number',
  client_env = 'Environment',
  version = 'Version',
  duration = 'Duration',
  poly_score = 'PolyScore',
  sort = 'Sort',
  offset = 'Offset',
  language = 'Language',
  language_freeform = 'Language',
  in_progress = 'In progress',
  statuses = 'Status',
  flows = 'Flows',
  last_modified = 'Last modified',
  reviewers = 'Reviewer',
  reviewed = 'Reviewed',
  variant_id = 'Variant',
  variant_id_freeform = 'Variant',
  conversation_id = 'Call SID',
  extension = 'Inbound route',
  fallback_route = 'Fallback route',
  function_calls = 'Function call',
  HANDOFF_REASON = 'Handoff reason',
  HANDOFF_TO = 'Handoff to',
  sort_alphabetically = 'Alphabetically',
  sort_last_updated = 'Last updated',
  sort_inactive = 'Inactive',
  sms_events = 'SMS events',
  QA = 'QA',
  cached_at = 'Cached date',
  cache_transcript = 'Transcript',
  hit_count = 'Play count',
  provider_voice_id = 'Provider voice ID',
  id = 'Cache ID',
  content_filters_triggered = 'Safety flag',
  metric_name = 'Metric name',
  metric_type = 'Metric type',
  metric_status = 'Status',
  batch_id = 'Group ID',
  latency = 'Turn latency',
  interruptions = 'Interruption',
  channel = 'Channel',
  direction_channel = 'Direction and channel',
  OUTBOUND_CALL_STATUS = 'Outbound call status',
  AB_GROUP_ASSIGNMENT = 'A/B Group Assignment',
  added_at = 'Date added',
  added_by = 'Added by',
  latest_outcome = 'Latest outcome',
  integration_category = 'Integration category',
}

export enum CommunicationChannelType {
  DIRECTION = 'direction',
  CHANNEL = 'channel',
}

export enum OrderType {
  ASC = 'asc',
  DESC = 'desc',
}

export interface SortQuery {
  id: string;
  order: OrderType;
  category: 'sort';
}

export enum BooleanOperator {
  eq = 'eq',
}

export enum NumberOperator {
  lt = 'lt',
  gt = 'gt',
  le = 'le',
  ge = 'ge',
  eq = 'eq',
  exists = 'exists',
}

export enum MultiSelectionOperator {
  in = 'in',
  ex = 'ex',
  eq = 'eq',
  exists = 'exists',
  notExists = 'notExists',
  contains = 'contains',
  notContains = 'not_contains',
}

export enum MetricsOperator {
  eq = 'eq',
  lt = 'lt',
  gt = 'gt',
  le = 'le',
  ge = 'ge',
  in = 'in',
  ex = 'ex',
  exists = 'exists',
}

export enum FreeFormSearchOperator {
  in = 'in',
  eq = 'eq',
  notContains = 'not_contains',
}

export enum ReadableTimeOp {
  lt = 'before',
  gt = 'after',
  le = 'before or on',
  ge = 'after or on',
  eq = 'is',
  exists = 'exists',
}

export enum ReadableNumberOp {
  lt = 'less than',
  gt = 'more than',
  le = 'less than or equals',
  ge = 'more than or equals',
  eq = 'is',
  exists = 'exists',
}

export enum ReadableOptionOp {
  in = 'includes',
  ex = 'excludes',
  eq = 'is',
  exists = 'has any value',
  notExists = 'has no value',
  contains = 'contains',
  not_contains = 'does not contain',
}

export enum ReadablePluralOptionOp {
  in = 'include',
  ex = 'exclude',
  eq = 'are exactly',
  exists = 'has any value',
  notExists = 'has no value',
  contains = 'contains',
  not_contains = 'does not contain',
}

export enum ReadableMetricsOperator {
  lt = 'is less than',
  gt = 'is greater than',
  le = 'is less than or equals to',
  ge = 'is greater than or equals to',
  eq = 'is',
  in = 'includes',
  ex = 'excludes',
  exists = 'exists',
  contains = 'contains',
  not_contains = 'does not contain',
}

export enum ReadableSearchOperator {
  in = 'contains',
  eq = 'is',
  not_contains = 'does not contain',
}

export enum EnvironmentType {
  SANDBOX = 'sandbox',
  LIVE = 'live',
  PRE_RELEASE = 'pre-release',
  DRAFT = 'draft',
}

export enum MetricInputType {
  int = 'int',
  float = 'float',
  bool = 'bool',
  string = 'string',
}

export type OptionsType = {
  value: string;
  label: string;
};

export type FilterOptionsType = {
  [key in StandardFilterType]?: OptionsType[];
};

export const LANGUAGE_CODE_MAP: { [key: string]: string } = {
  'en-gb': 'English (UK)',
  'en-us': 'English (US)',
  'fr-fr': 'French',
  'de-de': 'German',
  'es-es': 'Spanish',
  'it-it': 'Italian',
  'nl-nl': 'Dutch',
  zh: 'Chinese',
  'zh-tw': 'Chinese (Traditional)',
  'hr-hr': 'Croatian',
  'sr-rs': 'Serbian',
  'pl-pl': 'Polish',
  'pt-br': 'Portuguese (Brazil)',
  'pt-pt': 'Portuguese',
  'tr-tr': 'Turkish',
  'nl-be': 'Flemish',
  'ja-jp': 'Japanese',
  'iw-il': 'Hebrew',
  'sv-se': 'Swedish',
  'ko-kr': 'Korean',
};

export const MULTI_SELECTION_OPERATORS = [
  MultiSelectionOperator.eq,
  MultiSelectionOperator.ex,
  MultiSelectionOperator.in,
];

export const MULTI_SELECTION_WITH_EXISTENCE_OPERATORS = [
  MultiSelectionOperator.eq,
  MultiSelectionOperator.ex,
  MultiSelectionOperator.in,
  MultiSelectionOperator.exists,
  MultiSelectionOperator.notExists,
];

export const FUNCTION_CALL_OPERATORS = [
  MultiSelectionOperator.eq,
  MultiSelectionOperator.contains,
  MultiSelectionOperator.notContains,
  MultiSelectionOperator.exists,
  MultiSelectionOperator.notExists,
];

export const CONTENT_FILTER_TRIGGERED_OPERATORS = [
  MultiSelectionOperator.contains,
];

export const BATCH_ID_OPERATIONS = [MultiSelectionOperator.eq];

export const STRICT_STRING_MATCH_OPERATORS = [MultiSelectionOperator.eq];

export const STRING_MATCH_OPERATORS = [
  MultiSelectionOperator.eq,
  MultiSelectionOperator.contains,
  MultiSelectionOperator.notContains,
];

export const FREEFORM_OPERATORS = [
  MultiSelectionOperator.eq,
  MultiSelectionOperator.in,
  MultiSelectionOperator.ex,
];

export const FILTER_OPTION_MENU_STYLES: React.CSSProperties = {
  minWidth: '270px',
  width: 'fit-content',
  maxHeight: '50%',
  zIndex: 15,
  overflowY: 'auto',
};

export const clientEnvOptions = [
  EnvironmentType.LIVE,
  EnvironmentType.DRAFT,
  EnvironmentType.SANDBOX,
  EnvironmentType.PRE_RELEASE,
].map(env => ({ label: env, value: env }) as OptionsType);

export const SINGLE_USE_FILTERS = [
  StandardFilterType.SORT_ALPHABETICALLY,
  StandardFilterType.SORT_LAST_UPDATED,
  StandardFilterType.SORT_INACTIVE,
];

export const MUTUALLY_EXCLUSIVE_FILTERS = [
  [
    StandardFilterType.SORT_ALPHABETICALLY,
    StandardFilterType.SORT_LAST_UPDATED,
    StandardFilterType.SORT_INACTIVE,
  ],
];
